import * as THREE from 'three';
import { useEffect, useRef, useState } from 'react';
import {
  chestData,
  owlBodyElements,
  owlEyesElements,
  owlHeadElements,
  owlNonChangableElements,
  owlBeakElements,
  owlLegsElements
} from '../constants';
import { CDN_GATEWAY_BASE } from '../config';
import { UltraHDRLoader } from '../utils/ultrahdrloader';
import { optimiseMaterial } from '../components/GameSceneNew/utils/optimiseMaterial';

const textureLoader = new THREE.TextureLoader();
const environmentMap = textureLoader.load(`${CDN_GATEWAY_BASE}/images/map.png`);
environmentMap.mapping = THREE.EquirectangularReflectionMapping;

const ultraHdrLoader = new UltraHDRLoader();
const goldenEnvironmentMap = ultraHdrLoader.load(`${CDN_GATEWAY_BASE}/images/envmap.jpg`, (texture: THREE.Texture) => {
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.needsUpdate = true;
});

const onTextureLoaded = (texture: THREE.Texture) => {
  texture.colorSpace = "srgb";

  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.flipY = false;
  texture.needsUpdate = true;
};

const getTexturePromise = async (url: string) => {
  return new Promise<THREE.Texture>((resolve) => {
    textureLoader.load(url, (texture) => {
      onTextureLoaded(texture);
      resolve(texture);
    });
  });
};

export const useOwlSkins = (
  scene: THREE.Object3D,
  equippedSkins: any
) => {
  const [isPending, setIsPending] = useState<boolean>(false);

  const hideSkins = (scene: THREE.Object3D) => {
    scene.traverse((object: any) => {
      object.visible = false;
    });
  };

  const applyMaterial = (object: any) => {
    if (!object.material) {
      return;
    }

    object.material = object.material.clone();
    object.material.envMap = environmentMap;
  };

  const applyEyesSkin = async (object: any, skinIdByTrait: Record<string, string>) => {
    const skinId = skinIdByTrait.eyes;

    if (!skinId) {
      return;
    }

    if (object.material) {
      object.material.dispose();
    }

    const material = optimiseMaterial(new THREE.MeshStandardMaterial({
      normalMap: object.material?.normalMap,
    })) as THREE.MeshStandardMaterial;
    
    object.material = material;
    material.envMap = environmentMap;

    const mapTexturePath = `${CDN_GATEWAY_BASE}/textures/Eyes/Owl_low_T_Eyes_BaseColor_${skinId.replace(/\s/gi, '')}.png`;
    const roughnessTexturePath = `${CDN_GATEWAY_BASE}/textures/Eyes/Owl_low_T_Eyes_Roughness.png`;
    const metalnessTexturePath = `${CDN_GATEWAY_BASE}/textures/Eyes/Owl_low_T_Eyes_Metallic.png`;

    let hasCustomEyesSkin = false;

    try {
      hasCustomEyesSkin = (await fetch(mapTexturePath)).headers.get('content-type') === 'image/png';
    } catch {}

    if (hasCustomEyesSkin) {
      material.map = await getTexturePromise(mapTexturePath);
    } else {
      const defaultMapTexturePath = `${CDN_GATEWAY_BASE}/textures/Eyes/Owl_low_T_Eyes_BaseColor_Brown.png`;

      material.map = await getTexturePromise(defaultMapTexturePath);
    }

    material.roughnessMap = await getTexturePromise(roughnessTexturePath);
    material.metalnessMap = await getTexturePromise(metalnessTexturePath);

    material.roughness = 1.0;
    material.metalness = 0.0;
  };

  const applySkin = async (targetSkinSlot: string, object: any, skinIdByTrait: Record<string, string>) => {
    let skinId;

    if (targetSkinSlot === 'Body') {
      skinId = skinIdByTrait.body;
    }if (targetSkinSlot === 'Legs') {
      skinId = skinIdByTrait.body;
    } else if (targetSkinSlot === 'Head') {
      skinId = skinIdByTrait.head ?? skinIdByTrait.body;
    } else if (targetSkinSlot === 'Beak') {
      skinId = skinIdByTrait.body;
    } else if (targetSkinSlot === 'Eyes') {
      skinId = skinIdByTrait.eyes;
    }

    if (object.material) {
      object.material.dispose();
    }

    const material = optimiseMaterial(new THREE.MeshStandardMaterial({
      normalMap: object.material?.normalMap,
    })) as THREE.MeshStandardMaterial;
    
    object.material = material;
    material.envMap = environmentMap;

    let hasSkinFile = {
      map: false,
      normal: false,
      pbr: false,
    };

    const mapTexturePath = `${CDN_GATEWAY_BASE}/textures/${skinId}/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_BaseColor_${skinId}.png`;
    const normalMapTexturePath = `${CDN_GATEWAY_BASE}/textures/${skinId}/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_Normal_${skinId}.png`;
    const roughnessTexturePath = `${CDN_GATEWAY_BASE}/textures/${skinId}/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_Roughness_${skinId}.png`;
    const metalnessTexturePath = `${CDN_GATEWAY_BASE}/textures/${skinId}/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_Metallic_${skinId}.png`;

    try {
      hasSkinFile.map = (await fetch(mapTexturePath)).headers.get('content-type') === 'image/png';
    } catch {}

    try {
      hasSkinFile.normal = (await fetch(normalMapTexturePath)).headers.get('content-type') === 'image/png';
    } catch {}

    try {
      hasSkinFile.pbr = (await fetch(roughnessTexturePath)).headers.get('content-type') === 'image/png';
    } catch {}

    if (hasSkinFile.map) {
      material.map = await getTexturePromise(mapTexturePath);
    } else {
      const defaultMapTexturePath = `${CDN_GATEWAY_BASE}/textures/Brown/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_BaseColor_Brown.png`;
      material.map = await getTexturePromise(defaultMapTexturePath);
    }

    if (hasSkinFile.normal) {
      material.normalMap = await getTexturePromise(normalMapTexturePath);
    }

    if (hasSkinFile.pbr) {
      material.roughnessMap = await getTexturePromise(roughnessTexturePath);
      material.metalnessMap = await getTexturePromise(metalnessTexturePath);

      material.roughness = 1.0;
      material.metalness = 1.0;
    } else {
      const defaultRoughnessTexturePath = `${CDN_GATEWAY_BASE}/textures/Brown/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_Roughness_Brown.png`;
      const defaultMetalnessTexturePath = `${CDN_GATEWAY_BASE}/textures/Brown/${targetSkinSlot}/Owl_low_T_${targetSkinSlot}_Metallic_Brown.png`;

      material.roughnessMap = await getTexturePromise(defaultRoughnessTexturePath);
      material.metalnessMap = await getTexturePromise(defaultMetalnessTexturePath);
    }

    if (skinId?.includes('Golden')) {
      material.envMap = goldenEnvironmentMap;
      material.roughness = 0.0;
      material.metalness = 0.2;
    }
  };

  const applyWearable = async (object: any, wearableNodes: string[]) => {
    if (!wearableNodes) {
      return;
    }

    wearableNodes.forEach(objectId => {
      const targetObject = object.getObjectByName(objectId);

      if (!targetObject) {
        return;
      }

      targetObject.visible = true;
    });
  };

  const applySkinsToScene = async (
    equippedSkins: any,
    scene: any,
    options: { interrupted: boolean }
  ) => {
    scene.visible = false;
    setIsPending(true);

    const hasNoSkins = equippedSkins?.length === 0;
    const skinIdByTrait: Record<string, string> = {};

    hideSkins(scene);

    if (!hasNoSkins) {
      equippedSkins?.forEach((equippedItem: any) => {
        const { traits } = equippedItem.good;

        if (!traits) {
          return;
        }

        Object.entries(traits).forEach(([trait, value]) => {
          skinIdByTrait[trait] = value as string;
        });
      });
    }

    const skinsPromises: Promise<void>[] = [];

    scene.traverse((object: any) => {
      if (object === scene) {
        return;
      }

      const isBodyPart = owlBodyElements.includes(object.name);
      const isHeadPart = owlHeadElements.includes(object.name);
      const isEyesPart = owlEyesElements.includes(object.name);
      const isLegsPart = owlLegsElements.includes(object.name);
      const isBeakPart = owlBeakElements.includes(object.name);
      const isNonChangable = owlNonChangableElements.includes(object.name);

      applyMaterial(object);

      const targetSkinSlot = [
        isBodyPart && 'Body',
        isHeadPart && 'Head',
        isLegsPart && 'Legs',
        isBeakPart && 'Beak',
        isEyesPart && 'Eyes'
      ].filter(Boolean)[0];

      if (targetSkinSlot) {
        if (hasNoSkins) {
          if (isEyesPart) {
            skinsPromises.push(applyEyesSkin(object, skinIdByTrait));
          } else if (!isNonChangable) {
            skinsPromises.push(applySkin(targetSkinSlot!, object, skinIdByTrait));
          }
        } else {
          if (isEyesPart) {
            skinsPromises.push(applyEyesSkin(object, skinIdByTrait));
          } else {
            skinsPromises.push(applySkin(targetSkinSlot!, object, skinIdByTrait));
          }
        }
      }
    });

    if (skinIdByTrait.chest) {
      await applyWearable(scene, chestData[skinIdByTrait.chest]);
    }

    await Promise.all(skinsPromises);

    if (!options.interrupted) {
      scene.traverse((object: any) => {
        if (object === scene) {
          return;
        }

        const isBodyPart = owlBodyElements.includes(object.name);
        const isHeadPart = owlHeadElements.includes(object.name);
        const isEyesPart = owlEyesElements.includes(object.name);
        const isLegsPart = owlLegsElements.includes(object.name);
        const isBeakPart = owlBeakElements.includes(object.name);
        const isNonChangable = owlNonChangableElements.includes(object.name);

        const isOwlPart = isBodyPart || isHeadPart || isEyesPart || isLegsPart || isBeakPart || isNonChangable;

        if (isOwlPart) {
          let { parent } = object;

          while (parent) {
            if (!parent.visible && parent !== scene) {
              parent.visible = true;
            }
            parent = parent.parent;
          }

          object.visible = true;
        }
      });

      setIsPending(false);
      scene.visible = true;
    }
  };

  const lastInterruptRef = useRef({
    interrupted: false,
  });

  useEffect(() => {
    const equippedSkinsHash = JSON.stringify(equippedSkins);

    if (scene.userData.equippedSkinsHash === equippedSkinsHash) {
      return;
    }

    if (lastInterruptRef.current) {
      lastInterruptRef.current.interrupted = true;
    }

    lastInterruptRef.current = {
      interrupted: false,
    };

    scene.userData.equippedSkinsHash = equippedSkinsHash;

    applySkinsToScene(equippedSkins, scene, lastInterruptRef.current);
  }, [equippedSkins, scene]);

  return {
    isPendingSkins: isPending,
  };
};
